import { useEffect, useRef, useState } from "react";

/**
 * Full-bleed YouTube background using the YT IFrame JS API for reliable
 * error / state detection. Falls back to a poster image if the embed is
 * unavailable, blocked or autoplay fails.
 */
export default function YouTubeBackground({ videoId, start = 0, poster, className = "" }) {
  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [errored, setErrored] = useState(false);
  const [box, setBox] = useState({ w: 0, h: 0 });

  // Cover sizing — ResizeObserver
  useEffect(() => {
    if (!wrapperRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        const { width, height } = e.contentRect;
        const ratio = 16 / 9;
        let w = width;
        let h = width / ratio;
        if (h < height) {
          h = height;
          w = height * ratio;
        }
        const isMobile = width < 640;
        const overscan = isMobile ? 1.4 : 1.08;
        setBox({ w: w * overscan, h: h * overscan });
      }
    });
    ro.observe(wrapperRef.current);
    return () => ro.disconnect();
  }, []);

  // Load YT API and create player
  useEffect(() => {
    let cancelled = false;
    const loadAPI = () =>
      new Promise((resolve) => {
        if (window.YT && window.YT.Player) return resolve(window.YT);
        if (!document.getElementById("yt-iframe-api")) {
          const tag = document.createElement("script");
          tag.id = "yt-iframe-api";
          tag.src = "https://www.youtube.com/iframe_api";
          document.body.appendChild(tag);
        }
        const prev = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
          if (typeof prev === "function") prev();
          resolve(window.YT);
        };
        const it = setInterval(() => {
          if (window.YT && window.YT.Player) {
            clearInterval(it);
            resolve(window.YT);
          }
        }, 150);
      });

    loadAPI().then((YT) => {
      if (cancelled || !containerRef.current) return;
      try {
        playerRef.current = new YT.Player(containerRef.current, {
          host: "https://www.youtube-nocookie.com",
          videoId,
          playerVars: {
            autoplay: 1, mute: 1, controls: 0, showinfo: 0, modestbranding: 1,
            rel: 0, iv_load_policy: 3, disablekb: 1, fs: 0, playsinline: 1,
            start, loop: 1, playlist: videoId,
          },
          events: {
            onReady: (e) => { try { e.target.mute(); e.target.playVideo(); } catch (_) { /* noop */ } },
            onStateChange: (e) => {
              if (e.data === YT.PlayerState.PLAYING) setPlaying(true);
              if (e.data === YT.PlayerState.ENDED) {
                try { e.target.seekTo(start, true); e.target.playVideo(); } catch (_) { /* noop */ }
              }
            },
            onError: () => setErrored(true),
          },
        });
      } catch (_) {
        setErrored(true);
      }
    });

    return () => {
      cancelled = true;
      try { if (playerRef.current?.destroy) playerRef.current.destroy(); } catch (_) { /* noop */ }
    };
  }, [videoId, start]);

  // Safety: if not playing within 10s, fall back to poster
  useEffect(() => {
    const t = setTimeout(() => { if (!playing) setErrored(true); }, 10000);
    return () => clearTimeout(t);
  }, [playing]);

  const fallback = poster || `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`;
  const showVideo = playing && !errored;

  return (
    <div ref={wrapperRef} className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`} aria-hidden="true">
      <div
        className="absolute inset-0 w-full h-full bg-cover bg-center"
        style={{
          backgroundImage: `url("${fallback}")`,
          animation: !showVideo ? "kenburns 18s ease-in-out infinite alternate" : "none",
          opacity: showVideo ? 0 : 1,
          transition: "opacity 0.8s ease-out",
        }}
      />
      <div
        className="absolute left-1/2 top-1/2"
        style={{
          width: box.w || "100%",
          height: box.h || "100%",
          transform: "translate(-50%, -50%)",
          opacity: showVideo ? 1 : 0,
          transition: "opacity 0.7s ease",
        }}
      >
        {/* Container will be replaced by an iframe by the YT API */}
        <div ref={containerRef} className="w-full h-full" />
      </div>
      <style>{`
        @keyframes kenburns {
          0%   { transform: scale(1) translate(0, 0); }
          100% { transform: scale(1.12) translate(-2%, -1%); }
        }
      `}</style>
    </div>
  );
}
